/**
 * Helpers purs (client) pour l'import de la matrice des réserves CAC :
 * modèle Excel, parsing, validation ligne à ligne, résolution de la catégorie.
 * Colonnes : intitulé, reformulation, montant,
 * catégorie, récurrence. La catégorie est pilotée par le référentiel
 * CATEGORIE_RESERVE_COMMISSARIAT_AUX_COMPTES (aucune valeur codée en dur).
 * Extrait de `cac-api.service` (taille de fichier).
 */
import type ExcelJS from 'exceljs';
import type { ParameterValueSummary } from '../../../core/models/gouvernance/referentiel.model';
import type { ReserveCommissariatAuxComptesNestedRequest } from './models/cac-api.model';
import { refusTailleClasseur, texteCellule } from '../../../shared/core/utils/classeur.util';

/** En-têtes du modèle Excel « Matrice des réserves » (ordre des colonnes). */
export const COLONNES_TEMPLATE_RESERVE = ['INTITULE_RESERVE', 'REFORMULATION', 'MONTANT', 'CATEGORIE', 'RECURRENCE'] as const;

/** Ligne brute d'import matrice réserves (chaînes pour correction inline). */
export type LigneImportReserve = {
    ligne_excel: number;
    intitule_reserve: string;
    reformulation: string;
    montant: string;
    categorie: string;
    recurrence: string;
    erreurs: string[];
};

/** Normalise une chaîne pour comparaison souple (casse, accents, espaces). */
function normaliser(s: string): string {
    return s
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');
}

/**
 * Retrouve la valeur du référentiel CATEGORIE_RESERVE_COMMISSARIAT_AUX_COMPTES correspondant à une
 * saisie (code ou libellé, casse/accents tolérés). `null` si non reconnue.
 */
export function trouverCategorieReserve(saisie: string, categories: ParameterValueSummary[]): ParameterValueSummary | null {
    const s = normaliser(saisie);
    return categories.find((c) => normaliser(c.code) === s || normaliser(c.label) === s) ?? null;
}

/** Longueur maximale des champs texte d'une réserve (`CharField(max_length=255)` côté backend). */
const LONGUEUR_MAX_TEXTE_RESERVE = 255;

/** Valide une ligne d'import (intitulé, reformulation, montant, catégorie, récurrence). */
export function validerLigneReserve(l: LigneImportReserve, categories: ParameterValueSummary[]): string[] {
    const erreurs: string[] = [];
    if (!l.intitule_reserve.trim()) {
        erreurs.push('Intitulé obligatoire.');
    } else if (l.intitule_reserve.trim().length > LONGUEUR_MAX_TEXTE_RESERVE) {
        erreurs.push(`Intitulé : ${LONGUEUR_MAX_TEXTE_RESERVE} caractères maximum.`);
    }
    if (!l.reformulation.trim()) {
        erreurs.push('Reformulation obligatoire.');
    } else if (l.reformulation.trim().length > LONGUEUR_MAX_TEXTE_RESERVE) {
        erreurs.push(`Reformulation : ${LONGUEUR_MAX_TEXTE_RESERVE} caractères maximum.`);
    }
    // Le montant est obligatoire (BigIntegerField sans null ni défaut) : une réserve sans montant
    // n'est plus enregistrée à 0 en silence (audit R-18).
    if (!l.montant.trim()) {
        erreurs.push('Montant obligatoire.');
    } else {
        const n = Number(l.montant.replace(/\s/g, '').replace(',', '.'));
        if (!Number.isFinite(n) || n < 0) {
            erreurs.push('Montant : valeur numérique positive attendue.');
        } else if (!Number.isInteger(n)) {
            // Le backend stocke un entier strict (BigIntegerField) : refuser les décimales à la
            // saisie plutôt qu'en 400 après la validation de toute la matrice.
            erreurs.push('Montant : entier attendu, sans décimales.');
        }
    }
    if (!l.categorie.trim()) {
        erreurs.push('Catégorie obligatoire.');
    } else if (!trouverCategorieReserve(l.categorie, categories)) {
        const libelles = categories.map((c) => c.label).join(' / ');
        erreurs.push(`Catégorie : ${libelles || 'valeur du référentiel'}.`);
    }
    const rec = l.recurrence.trim().toUpperCase();
    if (rec && rec !== 'OUI' && rec !== 'NON') {
        erreurs.push('Récurrence : OUI ou NON.');
    }
    return erreurs;
}

/** Génère le modèle Excel « Matrice des réserves » (5 colonnes ; listes Catégorie/Récurrence dynamiques). */
export async function genererTemplateReserves(categories: ParameterValueSummary[]): Promise<Blob> {
    const Excel = (await import('exceljs')).default;
    const wb = new Excel.Workbook();
    wb.creator = 'SISPE — Suivi Performance';
    const ws = wb.addWorksheet('Réserves');
    ws.columns = COLONNES_TEMPLATE_RESERVE.map((c) => ({ header: c, key: c, width: 40 }));
    ws.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0C652E' } };
    const listeCategories = categories.map((c) => c.label).join(',');
    for (let r = 2; r <= 500; r++) {
        if (listeCategories) {
            ws.getCell(`D${r}`).dataValidation = { type: 'list', allowBlank: false, formulae: [`"${listeCategories}"`] };
        }
        ws.getCell(`E${r}`).dataValidation = { type: 'list', allowBlank: false, formulae: ['"OUI,NON"'] };
    }
    // Les listes déroulantes ci-dessus ont matérialisé les lignes 2 à 500 : `addRow` écrirait donc
    // l'exemple en 501, hors de vue et importé tel quel. On l'écrit dans la ligne 2 elle-même.
    const exemple = ws.getRow(2);
    const valeursExemple = [
        'Provisions pour litiges insuffisamment documentées',
        'Provisions pour litiges à documenter et justifier',
        125000000,
        categories[0]?.label ?? '',
        'OUI',
    ];
    valeursExemple.forEach((valeur, index) => {
        exemple.getCell(index + 1).value = valeur;
    });
    exemple.font = { italic: true, color: { argb: 'FF9CA3AF' } };
    const buffer = await wb.xlsx.writeBuffer();
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

/** Parse le fichier Excel « Matrice des réserves » en lignes brutes validées. */
export async function parserFichierReserves(file: File, categories: ParameterValueSummary[]): Promise<LigneImportReserve[]> {
    const Excel = (await import('exceljs')).default;
    const wb = new Excel.Workbook();
    const refus = refusTailleClasseur(file);
    if (refus) {
        throw new Error(refus);
    }
    await wb.xlsx.load(await file.arrayBuffer());
    const ws = wb.worksheets[0];
    if (!ws) {
        throw new Error('Le fichier ne contient aucune feuille.');
    }
    const lignes: LigneImportReserve[] = [];
    ws.eachRow((row: ExcelJS.Row, rowNumber: number) => {
        if (rowNumber === 1) {
            return;
        }
        // Formule, texte riche et lien lus par leur valeur (audit R-15).
        const cell = (col: number): string => texteCellule(row.getCell(col).value);
        const vals = [cell(1), cell(2), cell(3), cell(4), cell(5)];
        if (vals.every((x) => x === '')) {
            return;
        }
        // Catégorie canonicalisée vers le libellé du référentiel si reconnue (code/libellé/accents),
        // sinon saisie brute conservée pour affichage + message d'erreur.
        const cat = trouverCategorieReserve(vals[3], categories);
        const ligne: LigneImportReserve = {
            ligne_excel: rowNumber,
            intitule_reserve: vals[0],
            reformulation: vals[1],
            montant: vals[2],
            categorie: cat ? cat.label : vals[3],
            recurrence: vals[4],
            erreurs: [],
        };
        ligne.erreurs = validerLigneReserve(ligne, categories);
        lignes.push(ligne);
    });
    if (lignes.length === 0) {
        throw new Error('Aucune ligne de données trouvée dans le fichier.');
    }
    return lignes;
}

/** Montant d'une ligne validée ; refus explicite plutôt que 0 en silence (audit R-18). */
function montantReserve(l: LigneImportReserve): number {
    const montant = Number(l.montant.replace(/\s/g, '').replace(',', '.'));
    if (!l.montant.trim() || !Number.isFinite(montant) || montant < 0) {
        throw new Error(`Ligne ${l.ligne_excel} : montant absent ou invalide.`);
    }
    return montant;
}

/** Lignes validées de la matrice → corps `reserves[]` du PATCH nested du commissariat. */
export function versReservesNested(lignes: LigneImportReserve[], categories: ParameterValueSummary[]): ReserveCommissariatAuxComptesNestedRequest[] {
    return lignes.map((l) => ({
        categorie_id: trouverCategorieReserve(l.categorie, categories)?.id ?? '',
        intitule: l.intitule_reserve.trim(),
        reformulation: l.reformulation.trim(),
        montant: montantReserve(l),
        recurrence: l.recurrence.trim().toUpperCase() === 'OUI',
    }));
}
